/**
 * The trip, on a map, in the platform.
 *
 * The day cards answer "what is stop 3 on Thursday". They cannot answer "does
 * this trip make sense" - that is a shape question, and a list of addresses is
 * the wrong instrument for it. Handing someone off to Google Maps answers it
 * but in Google's clothes, in another tab, having lost the trip.
 *
 * So: same Clara Vale cartography as the Scheduling Console (same map id, so
 * the styling lives in one place in Google Cloud), stops numbered in visiting
 * order, and one line per day so the days read apart at a glance. Collapsed by
 * default - it is an answer to a question, not a thing to scroll past.
 *
 * Coordinates come free: every ItineraryStop persists the lat/lng it was
 * geocoded with at generation, so nothing is re-geocoded to draw this.
 */

import { useMemo } from 'react'
import { APIProvider, Map } from '@vis.gl/react-google-maps'
import { MapPin } from 'lucide-react'
import {
  FitBounds,
  NumberedMarkers,
  RoutePath,
  type LatLng,
} from '@/shared/components/maps/map-primitives'
import {
  CLARA_VALE_MAP_STYLES,
  MAPS_KEY,
  dayColour,
  missingMapConfig,
} from '@/shared/components/maps/map-config'
import type { ItineraryDay } from '@/admin/hooks/use-inspections'

type Pin = {
  key: string
  point: LatLng
  ordinal: number
  dayIndex: number
  label: string
  code: string
}

export function TripRouteMap({ days }: { days: ItineraryDay[] }) {
  const { pins, perDayPaths, allPoints } = useMemo(() => {
    const ordered = [...days].sort((a, b) => a.day_index - b.day_index)
    const pins: Pin[] = []
    const perDayPaths: { dayIndex: number; path: LatLng[] }[] = []
    let running = 0

    for (const day of ordered) {
      const path: LatLng[] = []
      for (const stop of day.stops) {
        running += 1
        // A stop that never geocoded has no place on a map. It still shows
        // in its day card, so it is not hidden - just not plottable.
        if (stop.lat == null || stop.lng == null) continue
        const point = { lat: stop.lat, lng: stop.lng }
        path.push(point)
        pins.push({
          key: stop.inspection_uuid,
          point,
          ordinal: running,
          dayIndex: day.day_index,
          label: stop.address || stop.code,
          code: stop.code,
        })
      }
      if (path.length) perDayPaths.push({ dayIndex: day.day_index, path })
    }
    return { pins, perDayPaths, allPoints: pins.map((p) => p.point) }
  }, [days])

  const missing = missingMapConfig()
  if (missing) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] py-12 text-center">
        <MapPin size={20} className="text-[var(--ink-3)]" />
        <p className="text-sm text-[var(--ink-2)]">
          Map unavailable - Google Maps is not fully configured here.
        </p>
        <p className="text-[11px] text-[var(--ink-3)]">
          Set <span className="font-mono">{missing}</span> to draw the route
          in the platform. The Google Maps links above work regardless.
        </p>
      </div>
    )
  }

  if (!pins.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] py-12 text-center">
        <MapPin size={20} className="text-[var(--ink-3)]" />
        <p className="text-sm text-[var(--ink-2)]">
          No stop on this trip has coordinates yet, so there is nothing to
          plot.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="h-[420px] overflow-hidden rounded-lg border border-[var(--rule)]">
        <APIProvider apiKey={MAPS_KEY}>
          <Map
            styles={CLARA_VALE_MAP_STYLES}
            defaultCenter={{ lat: 39.5, lng: -98.35 }}
            defaultZoom={4}
            disableDefaultUI
            zoomControl
            gestureHandling="greedy"
            clickableIcons={false}
          >
            <FitBounds points={allPoints} />
            {perDayPaths.map((d) => (
              <RoutePath
                key={`path-${d.dayIndex}`}
                path={d.path}
                strokeColor={dayColour(d.dayIndex)}
                strokeOpacity={0.85}
              />
            ))}
            <NumberedMarkers
              pins={pins.map((pin) => ({
                key: pin.key,
                point: pin.point,
                ordinal: pin.ordinal,
                colour: dayColour(pin.dayIndex),
                title: `${pin.ordinal}. ${pin.label}`,
              }))}
            />
          </Map>
        </APIProvider>
      </div>

      {/* Which colour is which day - without this the lines are decoration. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {perDayPaths.map((d) => (
          <span
            key={`legend-${d.dayIndex}`}
            className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)]"
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: dayColour(d.dayIndex) }}
            />
            Day {d.dayIndex}
          </span>
        ))}
        <span className="text-[11px] text-[var(--ink-3)]">
          Numbers are the visiting order across the whole trip.
        </span>
      </div>
    </div>
  )
}
